import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_URL from "../apiConfig";
import { useAuth } from "../context/AuthContext";

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M5 12h14M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M8 10V8a4 4 0 1 1 8 0v2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <rect
        x="6"
        y="10"
        width="12"
        height="10"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M5 12.5l4.2 4.2L19 7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatSystemInlineBold(text = "") {
  const parts = String(text || "").split(/(\*\*.*?\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function renderSystemDescription(text = "") {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((paragraph, index) => (
      <p key={index}>{formatSystemInlineBold(paragraph)}</p>
    ));
}

function getSystemIntro(text = "") {
  return String(text || "")
    .replace(/\*\*/g, "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)[0] || "";
}

export default function SystemActive() {
  const { systemId } = useParams();
  const navigate = useNavigate();
  const { user, initialized } = useAuth();

  const isInternalUser = user?.role === "ADMIN" || user?.role === "STAFF";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const session = JSON.parse(localStorage.getItem("zedro_session"));

        const res = await fetch(`${API_URL}/api/systems/${systemId}`, {
          headers: {
            Authorization: `Bearer ${session?.token}`,
            "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Montreal",
          },
        });

        const json = await res.json();

        setData(json);
      } catch (err) {
        console.error("ERROR SYSTEM:", err);
        setData({ ok: false });
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [systemId]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [systemId]);

  useEffect(() => {
    if (!initialized) return;

    if (isInternalUser) {
      setHasAccess(true);
      setCheckingAccess(false);
      return;
    }

    const checkAccess = async () => {
      try {
        const token = localStorage.getItem("zedro_session")
          ? JSON.parse(localStorage.getItem("zedro_session")).token
          : null;

        const res = await fetch(`${API_URL}/api/access/check`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: "system",
            resourceId: systemId,
          }),
        });

        const json = await res.json();
        setHasAccess(json.access);
      } catch (error) {
        console.error("ACCESS ERROR:", error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAccess();
  }, [systemId, initialized, isInternalUser]);

  const progressLookup = useMemo(() => {
    const map = new Map();
    (data?.progress || []).forEach((item) => {
      map.set(String(item.lesson_id || item.id), !!item.completed);
    });
    return map;
  }, [data]);

  const lessons = data?.lessons || [];

  const firstAvailableIncompleteIndex = useMemo(() => {
    const idx = lessons.findIndex((lesson) => {
      const completed = progressLookup.get(String(lesson.id));
      const lockedByTime = isInternalUser ? false : lesson.is_locked === true;

      return !completed && !lockedByTime;
    });

    return idx === -1 ? -1 : idx;
  }, [lessons, progressLookup, isInternalUser]);

  const totalLessons = lessons.length;

  const completedLessons = useMemo(() => {
    return lessons.filter((lesson) => progressLookup.get(String(lesson.id))).length;
  }, [lessons, progressLookup]);

  const completionPercent =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  const nextIncompleteIndex = useMemo(() => {
    const idx = lessons.findIndex((lesson) => {
      const completed = progressLookup.get(String(lesson.id));
      return !completed;
    });

    return idx === -1 ? -1 : idx;
  }, [lessons, progressLookup]);

  const currentLesson =
    firstAvailableIncompleteIndex >= 0
      ? lessons[firstAvailableIncompleteIndex]
      : nextIncompleteIndex >= 0
      ? lessons[nextIncompleteIndex]
      : null;

  const currentLessonLocked =
    currentLesson && !isInternalUser && currentLesson.is_locked === true;

  const orderedSections = useMemo(() => {
    const sectionsMap = {};

    lessons.forEach((lesson, lessonIndex) => {
      if (!sectionsMap[lesson.section_id]) {
        sectionsMap[lesson.section_id] = {
          id: lesson.section_id,
          title: lesson.section_title || "Sección",
          lessons: [],
        };
      }

      const completed = !!progressLookup.get(String(lesson.id));
      const locked = isInternalUser ? false : lesson.is_locked === true;
      const current =
        lessonIndex === firstAvailableIncompleteIndex &&
        !completed &&
        !locked;

      sectionsMap[lesson.section_id].lessons.push({
        ...lesson,
        lessonIndex,
        completed,
        locked,
        current,
      });
    });

    return Object.values(sectionsMap);
  }, [lessons, progressLookup, firstAvailableIncompleteIndex, isInternalUser]);

  if (checkingAccess) {
    return <div style={{ padding: 40 }}>Validando acceso...</div>;
  }

  if (hasAccess === false) {
    return <div style={{ padding: 40 }}>No tienes acceso a este sistema.</div>;
  }

  if (loading) {
    return <div style={{ padding: 40 }}>Cargando...</div>;
  }

  if (!data?.ok) {
    return <div style={{ padding: 40 }}>No hay sistema activo.</div>;
  }

  const system = data.system;

  return (
    <main className="z-system-shell">
      <button
        type="button"
        className="z-back-pill"
        onClick={() => navigate("/core")}
        style={{ marginBottom: "1.25rem" }}
      >
        ← Volver a Cimientos
      </button>

      <section className="z-system-hero">
        <div className="z-system-kicker">ZEDRO Core</div>

        <h1 className="z-system-title">{system?.title}</h1>

        {system?.description && (
          <div className="z-system-about">
            <button
              type="button"
              className="z-system-aboutToggle"
              onClick={() => setAboutOpen((value) => !value)}
            >
              <span>Sobre este sistema</span>
              <strong>{aboutOpen ? "−" : "+"}</strong>
            </button>

            {aboutOpen && (
              <div className="z-system-aboutContent">
                {renderSystemDescription(system.description)}
              </div>
            )}
          </div>
        )}

        <div className="z-system-progressWrap">
          <div className="z-system-progressMeta">
            <span>{completedLessons} de {totalLessons} días completados</span>
            <span>{completionPercent}%</span>
          </div>

          <div className="z-system-progressBar">
            <div
              className="z-system-progressFill"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>

        {currentLesson && (
          <div className="z-system-heroCard">
            <div>
              <div className="z-system-heroLabel">
                {currentLessonLocked
                  ? "Tu próximo paso"
                  : "Continúa tu proceso"}
              </div>
              <div className="z-system-heroDay">
                Semana{" "}
                {orderedSections.findIndex((section) =>
                  section.lessons.some((lesson) => lesson.id === currentLesson.id)
                ) + 1}
                {" · "}
                Día {currentLesson.day_number}: {currentLesson.title}
              </div>
            </div>

            <button
              type="button"
              className="z-system-heroBtn"
              disabled={Boolean(currentLessonLocked)}
              onClick={() => {
                if (!currentLesson || currentLessonLocked) return;
                navigate(`/core/day/${currentLesson.id}`);
              }}
            >
              {currentLessonLocked ? "Disponible mañana" : "Continuar"}
              <ArrowIcon />
            </button>
          </div>
        )}
      </section>

      <section className="z-system-sections">
        {orderedSections.map((section, sectionIndex) => {
          const availableCount = section.lessons.filter((lesson) => !lesson.locked).length;

          return (
            <details
              key={section.id || sectionIndex}
              className="z-system-sectionCard z-system-sectionAccordion"
              open={section.lessons.some((lesson) => lesson.current)}
            >
              <summary className="z-system-sectionSummary">
                <div className="z-system-sectionHead">
                  <div className="z-system-sectionKicker">
                    Semana {sectionIndex + 1}
                  </div>
                  <h2 className="z-system-sectionTitle">{section.title}</h2>
                  <div className="z-system-sectionMeta">
                    {availableCount} de {section.lessons.length} disponibles
                  </div>
                </div>
              </summary>

              <div className="z-system-daysGrid">
                {section.lessons.map((lesson) => {
                  const stateClass = lesson.completed
                    ? "is-completed"
                    : lesson.current
                    ? "is-current"
                    : lesson.locked
                    ? "is-locked"
                    : "is-available";

                  return (
                    <button
                      key={lesson.id}
                      type="button"
                      className={`z-system-dayCard ${stateClass}`}
                      disabled={lesson.locked}
                      onClick={() => {
                        if (lesson.locked) return;
                        navigate(`/core/day/${lesson.id}`);
                      }}
                    >
                      <div className="z-system-dayTop">
                        <div className="z-system-dayNumber">
                          Semana {sectionIndex + 1} · Día {lesson.day_number}
                        </div>

                        <div className="z-system-dayStatusIcon">
                          {lesson.completed ? (
                            <CheckIcon />
                          ) : lesson.locked ? (
                            <LockIcon />
                          ) : (
                            <span className="z-system-dayDot" />
                          )}
                        </div>
                      </div>

                      <div className="z-system-dayTitle">{lesson.title}</div>

                      {(lesson.completed || lesson.current || !lesson.locked) && (
                        <div className="z-system-dayMeta">
                          {lesson.completed
                            ? "Completado"
                            : lesson.current
                            ? "Disponible hoy"
                            : "Disponible"}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </details>
          );
        })}
      </section>
    </main>
  );
}
